import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import ListHeader from '@/modules/base/components/list/header/list-header-v2'
import ListQuickFilter from '@/modules/base/components/list/filter/list-quick-filter'
import List, { ListProps } from '@/modules/base/components/list/list-v2'
import Search from '@/modules/base/components/list/search/search'
import CreateModal from './create/modal'

import ClientContextMenu from './context-menu/client-context-menu'
import ListEmptyState from '@/modules/base/components/list/emptyState/emptyState'

import ListEmptyIcon from '@assets/images/emptyStates/client.svg'

import {
  fetchListSetting,
  updateColumnSort,
  updateColumnSetting,
  fetchList,
  refreshList,
} from '@/redux/ducks/lists/requests/clientList'

import IconAdd from '@assets/images/icons/add.svg'

import { LIST_TYPES } from '@/redux/ducks/lists'
import useCurrentModuleContext from '@/modules/_helpers/accessCheck/useCurrentModuleContext'
import useCheckIsAdminRole from '@/modules/_helpers/accessCheck/useCheckIsAdminRole'
import { ROLES } from '@/modules/base/util/constants'
import UserCellRenderer, { getUserLabel } from './cell-renderer/user-cell/user-cell'
import { getConnectionStatusLabel, getInvitationStatusLabel } from '@/modules/client/_helpers/getStatusLabel'
import { getCompanyFormGroupLabelByCompanyForm } from './create/company'
import InvitationStateCellRenderer from './cell-renderer/invitation-state-cell/invitation-state-cell'
import ConnectionStateCellRenderer from './cell-renderer/connection-state-cell/connection-state-cell'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import ListWrapper from '@/modules/base/components/list/list-wrapper'
import { isNumber } from 'lodash'
import { ConfirmModal } from '@/modules/base/components/modal'
import ClientConfig from '@/modules/client/config'
import { toast } from '@/modules/base/components/toast/toastConfig'
import request from '@/modules/base/util/request'
import { useNavigate } from 'react-router'
const { CLIENT_LIST_TYPE } = LIST_TYPES
const { resourceUrl } = ClientConfig

const exportSettings = {
  processCellCallback: (params) => {
    const { value, column: { colId } } = params
    switch (colId) {
      case 'clerks':
        return value.map((clerk) => getUserLabel(clerk)).join(', ')
      case 'contactUser':
        return getUserLabel(value?.[0])
      case 'inviteeStatus':
      case 'inviterStatus':
        return getInvitationStatusLabel(value)
      case 'connectionStatus':
        return getConnectionStatusLabel(value)
      default:
        return value
    }
  },
  csvExportFilename: lang.csvExportFileNameClients,
}

const processCellForClipboard = (params) => {
  const { value, column: { colId }, node: { data } } = params
  switch (colId) {
    case 'clerks':
      return value.map((clerk) => getUserLabel(clerk)).join(', ')
    case 'contactUser':
      return getUserLabel(value)
    case 'companyForm':
      return getCompanyFormGroupLabelByCompanyForm(value)
    case 'inviteeStatus':
    case 'inviterStatus':
      return getInvitationStatusLabel(value)
    case 'connectionStatus':
      return getConnectionStatusLabel(value)
    default:
      return data[colId] || value
  }
}

const useStyles = makeStyles(() => ({
  list: {
    flex: 1,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  search: {
    width: 230,
    height: 45,
    marginRight: 20,
    marginBottom: 6,
  },
}))

const ClientList = () => {
  const {
    lists: listState,
    listContextMenu: { data: selectedClientData, rowIndex },
  } = useSelector(state => state)
  const navigate = useNavigate()
  const { selectedNodes } = listState[CLIENT_LIST_TYPE]
  const [clientToDelete, setClientToDelete] = useState(null)
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const [gridApi, setGridApi] = useState(null)
  const module = useCurrentModuleContext()
  const dispatch = useDispatch()
  const classes = useStyles()
  const userInfo = useSelector(state => state.user.info)
  const { isOwner } = userInfo
  const isTaxConsultantAdmin = (userInfo?.roles || []).includes(ROLES.TAX_CONSULTANT_ROLE_ADMIN)
  const isUserOwnerOrAdmin = useCheckIsAdminRole() || isOwner || isTaxConsultantAdmin
  const canCreateClient = isUserOwnerOrAdmin

  useWebSocketEvent([EventTypes.COLLABORATION_UPDATED, EventTypes.COLLABORATION_STATUS_CHANGED], () => {
    dispatch(fetchListSetting())
  })

  useEffect(() => {
    dispatch(fetchListSetting())
  }, [])

  const onListReady = (api) => setGridApi(api)

  const onCreateClick = () =>
    setOpenCreateModal(true)

  const onSortChanged = (data) =>
    dispatch(updateColumnSort(data))

  const onColumnMoved = (data) =>
    dispatch(updateColumnSetting(data))

  const onSettingSave = (data) =>
    dispatch(updateColumnSetting(data, true))

  const headerButtons = [
    {
      label: lang.clientList.new,
      icon: IconAdd,
      onClick: onCreateClick,
      disabled: !module.rights.create || !canCreateClient,
    },
  ]

  const onRowClicked = ({ data }) => {
    const { id } = data
    navigate(`/client/${id}`)
  }

  const frameworkComponents = {
    invitationStateRenderer: InvitationStateCellRenderer,
    connectionStateRenderer: ConnectionStateCellRenderer,
    userCellRenderer: UserCellRenderer,
  }

  const contextMenu = isNumber(rowIndex) && <ClientContextMenu
    client={selectedClientData}
    rowIndex={rowIndex}
    readOnly={!module.rights.write}
    deleteReadOnly={!module.rights.delete}
    onDeleteClick={client => setClientToDelete(client)}
  />

  const handleCreateModalClose = () => {
    setOpenCreateModal(false)
    refreshList()
  }

  const deleteClientClick = async() => {
    try {
      await request(`${resourceUrl}/${clientToDelete.id}`, {
        method: 'DELETE',
        auth: true,
      })
      refreshList()
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.taxConsultant.errors.enable })
    }

    setClientToDelete(null)
  }

  const emptyState = () => (
    <ListEmptyState
      title={lang.clientList.emptyStateTitle}
      noSearchResultTitle={lang.clientList.listNoSearchResultTitle}
      visual={ListEmptyIcon}
      showButton
      buttonLabel={lang.clientList.buttonLabel}
      buttonClick={() => setOpenCreateModal(true)}
      disabled={!module.rights.create || !canCreateClient}
      rowModelType={ListProps.rowModelTypes.client.server}
      list={CLIENT_LIST_TYPE}
    />
  )

  return (
    <ListWrapper>
      <ListHeader
        title={lang.clientList.title}
        selectCount={selectedNodes?.length}
        buttons={headerButtons}
        gridApi={gridApi}
        onSettingSave={onSettingSave}
        exportSettings={exportSettings}
        listSettingName={CLIENT_LIST_TYPE}
      >
        <div className={classes.headerContainer}>
          <Search
            placeholder={lang.clientList.searchPlaceholder}
            customClass={classes.search}
            listSettingName={CLIENT_LIST_TYPE}
          />
          <ListQuickFilter listSettingName={CLIENT_LIST_TYPE} />
        </div>
      </ListHeader>
      <div className={classes.list}>
        <List
          components={frameworkComponents}
          rowIndex={rowIndex}
          onRowClicked={onRowClicked}
          showMenuContextMenu
          contextMenu={contextMenu}
          onListReady={onListReady}
          onColumnMoved={onColumnMoved}
          onSortChanged={onSortChanged}
          emptyText={lang.clientList.emptyStateText}
          emptyComponent={emptyState}
          processCellForClipboard={processCellForClipboard}
          listSettingName={CLIENT_LIST_TYPE}
          rowModelType={ListProps.rowModelTypes.server}
          dataSource={fetchList}
          loaderText={lang.clientList.loaderText}
        />
      </div>
      <CreateModal open={openCreateModal} onClose={handleCreateModalClose} />
      <ConfirmModal
        open={clientToDelete !== null}
        title={lang.clientDetail.widgets.overviewWidget.deleteClient}
        description={lang.clientDetail.widgets.overviewWidget.deleteClientDescription(clientToDelete?.name)}
        onConfirm={deleteClientClick}
        onAbort={() => setClientToDelete(null)}
        confirmButtonLabel={lang.generics.yes}
        abortButtonLabel={lang.generics.no}
      />

    </ListWrapper>
  )
}

export default ClientList
